import useScrollAnimation from '../../../hooks/useScrollAnimation';

const benefits = [
  {
    icon: 'ri-price-tag-3-line',
    title: 'Pago Único de S/20',
    desc: 'Olvídate de suscripciones mensuales. Pagas una vez, es tuyo para siempre. Sin letras pequeñas.',
    highlight: 'Para siempre',
    color: 'emerald',
  },
  {
    icon: 'ri-timer-flash-line',
    title: 'Listo en 5 minutos',
    desc: 'Diseñado para ser usado sin manuales. Sin curvas de aprendizaje, sin tutoriales de una hora.',
    highlight: 'Cero fricción',
    color: 'amber',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Garantía de Orden',
    desc: 'Si en 7 días no sientes que tu tienda está más organizada, te devolvemos tu inversión completa.',
    highlight: 'Riesgo CERO',
    color: 'red',
  },
];

const colorMap: Record<string, { icon: string; badge: string }> = {
  emerald: { icon: 'text-emerald-600 bg-emerald-100', badge: 'bg-emerald-100 text-emerald-700' },
  amber: { icon: 'text-amber-600 bg-amber-100', badge: 'bg-amber-100 text-amber-700' },
  red: { icon: 'text-red-500 bg-red-100', badge: 'bg-red-100 text-red-700' },
};

const OfertaSection = () => {
  const { ref, visible } = useScrollAnimation();

  return (
    <section id="oferta" className="py-16 sm:py-24 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div ref={ref} className={`text-center mb-8 sm:mb-10 ${visible ? 'anim-fade-up' : 'scroll-hidden'}`}>
          <span className="inline-block bg-amber-50 text-amber-600 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            La Oferta
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-gray-900 leading-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
            Una inversión mínima para{' '}
            <span className="text-emerald-600">un control máximo.</span>
          </h2>
        </div>

        {/* Main pricing card */}
        <div className={`relative bg-white border-2 border-emerald-200 rounded-3xl overflow-hidden ${visible ? 'anim-fade-up-1' : 'scroll-hidden'}`}>
          {/* Top badge */}
          <div className="bg-gray-900 text-center py-3 px-4">
            <span className="text-white text-xs sm:text-sm font-bold tracking-widest uppercase">
              Oferta de Lanzamiento — Tiempo Limitado
            </span>
          </div>

          <div className="p-6 sm:p-10 lg:p-14">
            {/* Price area */}
            <div className="text-center mb-8 sm:mb-12">
              <div className="flex items-start justify-center gap-2 mb-2">
                <span className="text-gray-300 line-through text-2xl sm:text-3xl font-bold mt-3 sm:mt-4">S/149</span>
                <span className="text-6xl sm:text-7xl lg:text-8xl font-black text-emerald-500" style={{ fontFamily: "'Syne', sans-serif" }}>S/20</span>
              </div>
              <p className="text-gray-500 text-sm sm:text-base">Un solo pago. Sin renovaciones. Sin sorpresas.</p>
              <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs sm:text-sm font-semibold px-4 sm:px-5 py-2 rounded-full mt-3">
                <i className="ri-money-dollar-circle-line" />
                Ahorras S/129 — más del 85% de descuento
              </div>
            </div>

            {/* Benefits grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
              {benefits.map((b) => {
                const c = colorMap[b.color];
                return (
                  <div key={b.title} className="bg-gray-50 rounded-2xl p-5 sm:p-6 text-center">
                    <div className={`w-11 h-11 flex items-center justify-center ${c.icon} rounded-xl mx-auto mb-3 sm:mb-4`}>
                      <i className={`${b.icon} text-xl sm:text-2xl`} />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-2">{b.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-500 leading-relaxed mb-3">{b.desc}</p>
                    <span className={`inline-block text-xs font-bold ${c.badge} px-3 py-1 rounded-full`}>
                      {b.highlight}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <div className="text-center">
              <button
                onClick={() => document.getElementById('checkout')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 sm:gap-3 bg-emerald-500 hover:bg-emerald-600 text-white text-base sm:text-xl font-bold px-8 sm:px-12 py-4 sm:py-5 rounded-xl transition-all cursor-pointer anim-pulse-green w-full sm:w-auto justify-center leading-snug"
              >
                <i className="ri-lock-line flex-shrink-0" />
                <span>¡Quiero mi acceso<br className="sm:hidden" /> por S/20 ahora!</span>
              </button>
              <p className="text-gray-400 text-xs sm:text-sm mt-3">
                <i className="ri-shield-check-line text-emerald-500 mr-1" />
                Garantía de devolución de 7 días — Sin preguntas
              </p>
            </div>
          </div>

          {/* Guarantee seal */}
          <div className="absolute bottom-6 right-8 hidden lg:flex items-center justify-center">
            <div className="w-24 h-24 border-2 border-dashed border-emerald-300 rounded-full flex flex-col items-center justify-center text-center">
              <i className="ri-shield-star-line text-emerald-500 text-2xl" />
              <span className="text-emerald-700 font-bold text-xs leading-tight">Garantía<br />7 días</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OfertaSection;
